import React, { useEffect, useState, useRef } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useRecoilState, useSetRecoilState, useRecoilValue, useRecoilCallback } from 'recoil';
import { atom_originalTourState, atom_tourState, atom_isDesktop, atom_fitBoundsRequest } from '@app/shared/state/tour.atom';
import { atom_filterState, atom_selectedFilterState } from '@app/shared/state/filter.atom';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_listModeState, atom_mobileListHeight } from '@app/shared/state/list-mode.atom';
import { flyAfterLayout } from '@app/shared/utils/fly-after-layout';
import { Tour } from '@app/shared/models/tour.type';
import { useTranslation } from 'react-i18next';
import { sideBarStateAtom } from '@app/shared/state/sidebar.atom';
import { useResizablePanel } from '@app/shared/hooks/useResizablePanel';
import { passiveCollector } from '@app/shared/services/passive-data/passive-data-collector';
import { Z_INDEX } from '@app/shared/styles/zIndex';


// Chip button component for filters
const FilterChip = ({
  label,
  icon,
  selected,
  onClick,
  isRTL
}: {
  label: string;
  icon?: string;
  selected: boolean;
  onClick: () => void;
  isRTL: boolean;
}) => (
  <Button
    variant={selected ? "contained" : "outlined"}
    size="small"
    onClick={onClick}
    sx={{
      width: '106px',
      height: '32px',
      gap: '4px',
      opacity: 1,
      backgroundColor: selected ? '#48526B' : 'white',
      borderColor: '#E0E0E0',
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '13px',
      px: 2,
      py: 1,
      borderRadius: '16px',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: selected ? '#1a1f2e' : '#f5f5f5',
        borderColor: '#E0E0E0',
      },
    }}
  >
    {icon && (
      <i
        className={`fa-regular fa-${icon}`}
        style={{
          marginInlineEnd: '5px',
          fontSize: '12px'
        }}
      />
    )}
    <Typography sx={{ fontWeight: 700, fontSize: '12px', lineHeight: '16px', letterSpacing: '0px', color: 'inherit', }}>
      {label}
    </Typography>
  </Button>
);

interface FilterContentProps {
  contentRef?: React.RefObject<HTMLDivElement>;
  preventMapInteraction?: (e: React.MouseEvent | React.TouchEvent) => void;
  initiallyVisible?: boolean;
}

export const FilterContent: React.FC<FilterContentProps> = ({
  contentRef,
  preventMapInteraction,
  initiallyVisible,
}) => {
  const { t, i18n } = useTranslation();
  const activeTour = useRecoilValue(atom_tourState);
  const [originalTour, setOriginalTour] = useRecoilState(atom_originalTourState);
  const [filters, setFilters] = useRecoilState(atom_filterState);
  const [selectedFilters, setSelectedFilters] = useRecoilState(atom_selectedFilterState);
  const setActiveStopState = useSetRecoilState(atom_activeStopState);
  const setListMode = useSetRecoilState(atom_listModeState);
  const setFitBoundsRequest = useSetRecoilState(atom_fitBoundsRequest);
  const isDesktop = useRecoilValue(atom_isDesktop);
  const hasSidebar = String(activeTour?.config?.sidebar) !== 'false';
  const [sideBarState, setSideBarState] = useRecoilState(sideBarStateAtom);

  // Ref to measure content height
  const contentBoxRef = useRef<HTMLDivElement>(null);

  // Min constraint
  const minHeight = 150;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const maxHeight = Math.floor(viewportHeight * 0.8);

  const [contentMaxHeight, setContentMaxHeight] = useState<number>(0);
  const [isInitialized, setIsInitialized] = useState(false);

  const { currentHeight, setCurrentHeight, isDragging, dragHandleProps, containerTransitionStyle, isMounted, startExit } = useResizablePanel({
    minHeight,
    maxHeight: contentMaxHeight || maxHeight,
    initialHeight: 0,
    initiallyVisible,
  });

  // Detect RTL language (Hebrew)
  const isRTL = i18n.language === 'he';

  // Save filter state when panel opens, to restore on close (X) without "Show Results"
  const [initialFilters] = useState<string[]>(() => [...selectedFilters]);
  const [initialTourState] = useState<Tour | undefined>(() => activeTour);

  // Measure content and set initial height to content + 80px (clamped to maxHeight)
  useEffect(() => {
    if (!isDesktop && contentBoxRef.current && !isInitialized) {
      const measureHeight = () => {
        if (contentBoxRef.current) {
          const measuredHeight = contentBoxRef.current.scrollHeight;
          const viewportMaxHeight = Math.floor(viewportHeight * 0.8);
          const clampedMaxContentHeight = Math.min(measuredHeight, viewportMaxHeight);
          const initialHeight = Math.floor(viewportHeight * 0.5);
          setContentMaxHeight(clampedMaxContentHeight);
          setCurrentHeight(initialHeight);
          setIsInitialized(true);
        }
      };
      requestAnimationFrame(measureHeight);
    }
  }, [isDesktop, filters, isInitialized]);

  // Calculate filtered results count
  const getFilteredResultsCount = () => {
    if (selectedFilters.length === 0) {
      return originalTour?.stops?.length || 0;
    }
    return (originalTour?.stops || []).filter(stop =>
      stop?.tags?.some(tag => selectedFilters.includes(tag.text))
    ).length;
  };

  // Set originalTour only once when activeTour first loads
  useEffect(() => {
    if (activeTour && !originalTour) {
      setOriginalTour(activeTour);
    }
  }, [activeTour, originalTour, setOriginalTour]);

  // Extract available filters from tour stops
  useEffect(() => {
    if (filters.length > 0) {
      return;
    }
    const tourToSearch = originalTour || activeTour;
    const tags = tourToSearch?.stops.reduce((acc, nextStop) => {
      nextStop?.tags?.map(tag => !!tag.text && acc.add(tag.text));
      return acc;
    }, new Set<string>()) as Set<string>;
    if (tags?.size > 0) {
      setFilters([...new Array(...tags)]);
    }
  }, [activeTour, originalTour, filters.length, setFilters]);

  // Get icon for a filter tag
  const textToIcon = (text: string) => {
    const tourToSearch = originalTour || activeTour;
    const tags = tourToSearch?.stops.reduce((acc, nextStop) => {
      const arr = nextStop?.tags || [];
      for (const tag of arr) {
        const tagObj: { text: string, icon: string } = { text: tag.text, icon: tag.icon };
        acc.push(tagObj);
      }
      return acc;
    }, [] as { text: string, icon: string }[]);
    return tags?.find(tag => tag.text === text)?.icon;
  };

  // Apply filters immediately when clicked (selector_visibleStops handles filtering)
  const handleFilterClick = (filter: string) => {
    const newSelectedFilters = selectedFilters.includes(filter)
      ? selectedFilters.filter(f => f !== filter)
      : [...selectedFilters, filter];

    setActiveStopState(undefined);
    setSelectedFilters(newSelectedFilters);
  };

  const handleClearAll = () => {
    setSelectedFilters([]);
    setActiveStopState(undefined);
  };

  // Helper function to count how many selected filters a stop matches
  const countMatchingFilters = (stop: Tour['stops'][0], filters: string[]): number => {
    if (!stop?.tags || filters.length === 0) return 0;
    return stop.tags.filter(tag => filters.includes(tag.text)).length;
  };

  // Defer-time read of mobileListHeight so we capture the post-mount value of
  // the destination mobileList panel, not the soon-to-unmount filter panel.
  const getLiveMobileListHeight = useRecoilCallback(({ snapshot }) => () =>
    (snapshot.getLoadable(atom_mobileListHeight).contents as number) || 0
  , []);

  // Handle "Show results" button - filters already applied via selector_visibleStops
  const handleShowResults = () => {
    setSideBarState(null);
    selectedFilters.forEach(tag => passiveCollector.trackFilterApplied(tag));

    // Compute bbox up-front (data doesn't depend on layout)
    let bounds: [[number, number], [number, number]] | null = null;
    if (!isDesktop && selectedFilters.length > 0) {
      const filteredStops = (originalTour?.stops || []).filter(stop =>
        stop?.tags?.some(tag => selectedFilters.includes(tag.text))
      );
      if (filteredStops.length > 0) {
        let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
        filteredStops.forEach(stop => {
          const lat = stop.stop_location?.latitude;
          const lng = stop.stop_location?.longitude;
          if (lat != null && lng != null) {
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
          }
        });
        if (minLat !== Infinity) {
          bounds = [[minLat, minLng], [maxLat, maxLng]];
        }
      }
    }

    setListMode(isDesktop ? 'desktop' : 'mobileList');

    // Defer the fit-bounds dispatch so the new mobileList panel mounts and
    // measures itself first — then atom_mobileListHeight reflects the panel
    // the user will actually see, not the filter panel that's unmounting.
    if (bounds) {
      flyAfterLayout(() => {
        const panelHeight = getLiveMobileListHeight()
          || Math.floor(viewportHeight * 0.25) + 100; // fallback if measure missed
        setFitBoundsRequest({
          bounds: bounds!,
          timestamp: Date.now(),
          bottomPadding: panelHeight,
        });
      });
    }
  };

  // Close without applying - restore filters to their state before panel was opened
  const handleClose = () => {
    setSelectedFilters(initialFilters);
    setActiveStopState(undefined);
    setListMode(isDesktop ? 'desktop' : 'mobileList');
  };

  // Prevent map interaction handler (fallback if not provided)
  const handlePreventMapInteraction = preventMapInteraction || ((e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
  });

  // Content box (shared between desktop and mobile)
  const contentBox = (
    <Box
      ref={contentBoxRef}
      dir={isRTL ? 'rtl' : 'ltr'}
      sx={{
        boxShadow: !isDesktop ? '0px 0px 10px 0px rgba(0,0,0,0.15)' : 'none',
        bgcolor: !isDesktop ? '#F6F6F6' : 'white',
        alignSelf: !isDesktop ? 'center' : 'auto',
        width: '100%',
        maxWidth: !isDesktop ? '530px' : '100%',
        height: isDesktop ? '100%' : (isInitialized ? `${currentHeight}px` : 'auto'),
        maxHeight: isDesktop ? '100%' : '85vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        textAlign: isRTL ? 'right' : 'left',
        position: !isDesktop ? 'fixed' : 'none',
        zIndex: !isDesktop ? Z_INDEX.modal : 'auto',
        borderRadius: !isDesktop ? '20px 20px 0px 0px' : '0px',
        bottom: !isDesktop && hasSidebar ? 0 : -60,
        left: !isDesktop ? 0 : 'auto',
        right: !isDesktop ? 0 : 'auto',
        paddingBottom: 0,
        touchAction: 'none',
        pointerEvents: 'auto',
        isolation: 'isolate',
        ...(isDesktop ? {} : containerTransitionStyle),
        transform: !isDesktop
          ? (isMounted ? 'translateZ(0) translateY(0)' : 'translateZ(0) translateY(100%)')
          : 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
      onClick={handlePreventMapInteraction}
      onMouseDown={handlePreventMapInteraction}
      onMouseMove={handlePreventMapInteraction}
    >
      {/* Drag Handle (mobile only) */}
      {!isDesktop && isInitialized && (
        <Box
          {...dragHandleProps}
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'ns-resize',
            touchAction: 'none',
            padding: '10px 0',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 40px',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(95,93,92,1)',
                height: '2px',
                width: '60px',
                borderRadius: '8px',
                mb: 0.5,
              }}
            />
            <Box
              sx={{
                bgcolor: 'rgba(95,93,92,1)',
                height: '2px',
                width: '60px',
                borderRadius: '8px',
              }}
            />
          </Box>
        </Box>
      )}
      {/* Filter Tags Section */}
      <Box
        ref={contentRef}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
        sx={{
          overflowY: 'auto',
          px: 2,
          mt: 0,
          pt: 1.5,
          pb: isDesktop ? 2 : '140px',
          flex: isDesktop ? 'none' : 1,
          minHeight: isDesktop ? 'auto' : 0,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          touchAction: 'pan-y',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
          {filters.map((filter) => (
            <FilterChip
              key={filter}
              label={filter.charAt(0).toUpperCase() + filter.slice(1)}
              icon={textToIcon(filter)}
              selected={selectedFilters.includes(filter)}
              onClick={() => handleFilterClick(filter)}
              isRTL={isRTL}
            />
          ))}
        </Box>
        {filters.length === 0 && (
          <Typography sx={{ color: '#666', fontSize: '13px', fontStyle: 'italic' }}>
            {t('no_filters_available') || 'No filters available'}
          </Typography>
        )}
      </Box>

      {/* Footer with Clear all and Show results buttons */}
      <Box sx={{
        ...(!isDesktop ? {
          position: 'fixed',
          width: '100%',
          bottom: 60,
          left: 0,
        } : {
          position: 'relative',
          width: '100%',
        }),
        height: 60,
        display: 'flex',
        flexDirection: isRTL ? 'row-reverse' : 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2,
        py: 2,
        borderTop: '1px solid #E3E3E3',
        borderBottom: isDesktop ? '1px solid #E3E3E3' : 'none',
        backgroundColor: 'white',
      }}>
        <Button
          onClick={selectedFilters.length > 0 ? handleClearAll : handleClose}
          variant="text"
          disableRipple
          sx={{
            backgroundColor: 'transparent',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            letterSpacing: '0.5px',
            minWidth: 'auto',
            mx: '10px',
            p: 0,
            color: '#48526B',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          {selectedFilters.length > 0 ? (t('clear_all') || 'Clear all') : (t('exit') || 'Exit')}
        </Button>
        <Button
          onClick={handleShowResults}
          variant="contained"
          sx={{
            backgroundColor: '#D0552E',
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            px: 3,
            py: 1,
            borderRadius: '24px',
            '&:hover': {
              backgroundColor: '#b84a28',
            }
          }}
        >
          {t('show_results') || 'Show results'} ({getFilteredResultsCount()})
        </Button>
      </Box>
    </Box>
  );

  // Both desktop and mobile now use the same contentBox
  // Mobile has drag handle included inside contentBox
  return contentBox;
};

export default FilterContent;
